"use client";
/**
 * Hooks for the Block 4 offer surfaces: the "Your offers" inbox and the
 * inline next-best-offer cross-sell (Home carousel / Checkout card / post-order
 * card).
 *
 * Everything here is fail-soft by design (overview 7.5/7.6 + cross-cutting
 * requirement 5: "If ranker errors out, surfaces fall back ... never blank"):
 *  - signed-out → idle, no fetch, empty data
 *  - 404 (endpoint not deployed) → empty data, `unavailable: true`
 *  - any next-best transport error → empty list (the api wrapper swallows it)
 *
 * Per-session dismissal of carousel/cross-sell cards is tracked in
 * sessionStorage so a dismissed offer stays gone for the tab's lifetime.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  offersApi,
  type NextBestContext,
  type NextBestOffer,
  type OffersInboxResponse,
  type OfferSurface,
} from "@/api/services";
import { ApiError } from "@/api/http";
import { useAuth } from "@/store/auth";
import { scopedStorageKey } from "@/lib/storageScope";

/* ──────── Your offers inbox ──────── */

export type UseOffersInboxResult = {
  loading: boolean;
  error: Error | null;
  inbox: OffersInboxResponse;
  /** True when the endpoint 404'd (backend not deployed yet). */
  unavailable: boolean;
  /** Total active + upcoming + expired — useful for the Home badge count. */
  totalCount: number;
  /** Active count alone — what the Home bell badge should show. */
  activeCount: number;
  refresh: () => Promise<void>;
};

const EMPTY_INBOX: OffersInboxResponse = { active: [], upcoming: [], expired: [] };

export function useOffersInbox(): UseOffersInboxResult {
  const { isLoggedIn, user } = useAuth();
  const [inbox, setInbox] = useState<OffersInboxResponse>(EMPTY_INBOX);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setInbox(EMPTY_INBOX);
      setError(null);
      setLoading(false);
      setUnavailable(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await offersApi.getInbox(user.id);
      setInbox(res);
      // getInbox returns the empty shape on 404; treat a fully-empty result as
      // simply "no offers" (not an error). We only flag `unavailable` for a
      // hard 404 we can detect — but since the api swallows it, an empty inbox
      // is presented through the normal empty state, which is the desired UX.
      setUnavailable(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setInbox(EMPTY_INBOX);
        setUnavailable(true);
        setError(null);
      } else {
        console.warn("[offers] inbox fetch failed", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setInbox(EMPTY_INBOX);
        setUnavailable(false);
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeCount = inbox.active.length;
  const totalCount = inbox.active.length + inbox.upcoming.length + inbox.expired.length;

  return { loading, error, inbox, unavailable, totalCount, activeCount, refresh };
}

/* ──────── Next-best-offer ranker (carousel / cross-sell / post-order) ──────── */

export type UseNextBestResult = {
  loading: boolean;
  /** Ranked offers from the ranker (already capped per surface server-side). */
  offers: NextBestOffer[];
  refresh: () => Promise<void>;
};

/**
 * Fetch ranked next-best offers for a surface. Always resolves (never throws)
 * — the api wrapper returns `[]` on any error. `cartCategoryIds` is only sent
 * for the checkout cross-sell surface where the ranker can match add-ons.
 *
 * Pass `enabled: false` to defer the call (e.g. on a screen that hasn't mounted
 * its data yet); the hook then stays idle and returns no offers.
 */
export function useNextBestOffers(
  surface: OfferSurface,
  opts: {
    branchId?: string | null;
    cartCategoryIds?: string[] | null;
    enabled?: boolean;
  } = {},
): UseNextBestResult {
  const { isLoggedIn, user } = useAuth();
  const { branchId = null, cartCategoryIds = null, enabled = true } = opts;
  const [offers, setOffers] = useState<NextBestOffer[]>([]);
  const [loading, setLoading] = useState(false);

  // Stabilise the category list so it doesn't refire the effect every render.
  const categoryKey = useMemo(
    () => (cartCategoryIds && cartCategoryIds.length ? [...cartCategoryIds].sort().join(",") : ""),
    [cartCategoryIds],
  );

  const refresh = useCallback(async () => {
    if (!enabled || !isLoggedIn || !user?.id) {
      setOffers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const context: NextBestContext = {
      surface,
      branchId: branchId ?? undefined,
      cartCategoryIds: categoryKey ? categoryKey.split(",") : undefined,
    };
    const res = await offersApi.nextBest(user.id, context);
    setOffers(res);
    setLoading(false);
  }, [enabled, isLoggedIn, user?.id, surface, branchId, categoryKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, offers, refresh };
}

/* ──────── Per-session dismissal (carousel + cross-sell) ──────── */

const DISMISSED_OFFERS_KEY = "v2.offers.dismissed";

function dismissalStorageKey(slug?: string | null): string {
  return scopedStorageKey(DISMISSED_OFFERS_KEY, slug);
}

function readDismissed(slug?: string | null): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(dismissalStorageKey(slug));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : []);
  } catch {
    return new Set();
  }
}

/**
 * Tracks which offers the user dismissed this session. Keyed per-promotion so a
 * dismissed Home-carousel card and the same offer at checkout are independent
 * only if you scope the id yourself — by default a promotionId dismissal hides
 * it everywhere this hook is consulted, which matches "dismissable per-card"
 * for the lifetime of the tab.
 */
export function useSessionDismissal(slug?: string | null) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed(slug));

  // Re-read when the slug changes (tenant switch within the same tab).
  useEffect(() => {
    setDismissed(readDismissed(slug));
  }, [slug]);

  const dismiss = useCallback(
    (id: string) => {
      setDismissed((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        if (typeof window !== "undefined") {
          try {
            window.sessionStorage.setItem(
              dismissalStorageKey(slug),
              JSON.stringify([...next]),
            );
          } catch {
            // sessionStorage unavailable — non-fatal, dismissal stays in-memory.
          }
        }
        return next;
      });
    },
    [slug],
  );

  const isDismissed = useCallback((id: string) => dismissed.has(id), [dismissed]);

  return { dismiss, isDismissed, dismissed };
}
